import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem,
  IconButton, Modal, Button, Chip, FormControl, InputLabel, Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

export default function AdminSupportPage() {
  const [supportList, setSupportList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');

  useEffect(() => {
    axios.get("/api/support")
      .then(res => {
        setSupportList(res.data);
      })
      .catch(err => console.error('Destek talepleri alınamadı:', err));
  }, []);

  useEffect(() => {
    const filtered = supportList.filter(item =>
      statusFilter ? item.status === statusFilter : true
    );
    setFilteredList(filtered);
  }, [supportList, statusFilter]);

  const totalCount = supportList.length;
  const openCount = supportList.filter(req => req.status === 'open').length;

  const handleView = (row) => {
    setSelectedRequest(row);
    setModalOpen(true);
    setAdminMessage('');
  };

  const handleSendMessage = async () => {
    if (!adminMessage.trim()) return;
    const updatedMessages = [
      ...selectedRequest.messages,
      {
        sender: 'admin',
        content: adminMessage,
        timestamp: new Date().toISOString()
      }
    ];
    setSelectedRequest({ ...selectedRequest, messages: updatedMessages });
    try {
      await axios.post(`}/api/support/${selectedRequest._id}/message`, {
        sender: 'admin',
        content: adminMessage
      });
      const updated = await axios.get("/api/support");
      setSupportList(updated.data);
      setAdminMessage('');
    } catch (err) {
      console.error('Mesaj gönderilemedi:', err);
    }
  };

  const handleCloseRequest = async () => {
    try {
      await axios.put(`/api/support/${selectedRequest._id}/status`, {
        status: 'closed'
      });
      const updated = await axios.get("/api/support");
      setSupportList(updated.data);
      setModalOpen(false);
    } catch (err) {
      console.error('Talep kapatılamadı:', err);
    }
  };
  const handleReopenRequest = async () => {
    try {
      await axios.put(`/api/support/${selectedRequest._id}/status`, {
        status: 'open'
      });
      const updated = await axios.get("/api/support");
      setSupportList(updated.data);
      const reopened = updated.data.find(r => r._id === selectedRequest._id);
      setSelectedRequest(reopened);
    } catch (err) {
      console.error('Talep açılamadı:', err);
    }
  };


  const columns = [
    { field: 'userName', headerName: 'Kullanıcı Adı', flex: 1 },
    { field: 'userId', headerName: 'Kullanıcı ID', flex: 1 },
    {
      field: 'messages',
      headerName: 'Son Mesaj',
      flex: 2,
      renderCell: (params) => {
        const msgs = params?.row?.messages || [];
        const last = msgs.at(-1);
        const content = last?.content || '';
        const preview = content.length > 90
          ? content.slice(0, 90) + '...'
          : content;

        const isUserLast = last.sender === 'user';
        const isClosed = params.row.status === 'closed';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
            {/* Mesaj preview */}
            {preview}

            {/* Eğer son mesaj kullanıcıdan geldiyse uyarı */}
            {isUserLast && !isClosed && (
              <Chip
                label="Yeni"
                color="error"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  px: 1,
                  ml: 2
                }}
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={params.value === 'open' ? 'Açık' : 'Kapalı'}
          color={params.value === 'open' ? 'info' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      flex: 0.6,
      renderCell: (params) => (
        <IconButton onClick={() => handleView(params.row)}>
          <VisibilityIcon />
        </IconButton>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Dashboard Cards */}
      <Box sx={{
        display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap'
      }}>
        <Card sx={{ width: 220, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <QuestionAnswerIcon sx={{ fontSize: 40, color: '#00bcd4' }} />
            <Typography variant="subtitle2" color="text.secondary" mt={1}>Açık Talepler</Typography>
            <Typography variant="h5" fontWeight={600}>{openCount}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ width: 220, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <SupportAgentIcon sx={{ fontSize: 40, color: '#1976d2' }} />
            <Typography variant="subtitle2" color="text.secondary" mt={1}>Toplam Talep</Typography>
            <Typography variant="h5" fontWeight={600}>{totalCount}</Typography>
          </CardContent>
        </Card>

      </Box>

      {/* Filtre */}
      <FormControl sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel shrink id="status-filter-label">Durum filtresi</InputLabel>
        <Select
          labelId="status-filter-label"
          value={statusFilter}
          sx={{ mt: 2 }}
          onChange={(e) => setStatusFilter(e.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return "Hepsi";
            if (selected === 'open') return "Açık";
            if (selected === 'closed') return "Kapalı";
            return selected;
          }}
        >
          <MenuItem value="">Hepsi</MenuItem>
          <MenuItem value="open">Açık</MenuItem>
          <MenuItem value="closed">Kapalı</MenuItem>
        </Select>
      </FormControl>

      {/* Liste */}
      <Box sx={{ width: '97%', overflowX: 'auto' }}>
        <div style={{ minWidth: 800, height: 500 }}>
          <DataGrid
            rows={filteredList.map((f) => ({ id: f._id, ...f }))}
            columns={columns}
            pageSize={10}
          />
        </div>
      </Box>

      {/* Modal: Mesajlar ve Cevap */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={{
          p: 4, bgcolor: 'white', mx: 'auto', my: '5%', width: 500, borderRadius: 2, position: 'relative'
        }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => setModalOpen(false)}
          >
            X
          </IconButton>

          <Typography variant="h6" gutterBottom>Destek Konuşması</Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
            {selectedRequest?.messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: msg.sender === 'admin' ? 'right' : 'left',
                  mb: 1
                }}
              >
                <Box sx={{
                  display: 'inline-block',
                  px: 2,
                  py: 1,
                  bgcolor: msg.sender === 'admin' ? '#e3f2fd' : '#f1f8e9',
                  borderRadius: 2,
                  maxWidth: '80%'
                }}>
                  <Typography variant="body2">{msg.content}</Typography>
                  <Typography variant="caption" display="block" color="gray">
                    {new Date(msg.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <TextField
            label="Yanıt Yaz"
            fullWidth
            multiline
            rows={2}
            value={adminMessage}
            onChange={(e) => setAdminMessage(e.target.value)}
            disabled={selectedRequest?.status === 'closed'}
          />

          <Box mt={2} display="flex" justifyContent="space-between">
            {selectedRequest?.status === 'closed' ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleReopenRequest}
              >
                Talebi Aç
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCloseRequest}
              >
                Talebi Kapat
              </Button>
            )}

            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={selectedRequest?.status === 'closed'}
            >
              Yanıtla
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

